import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronRight, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = "epidemos_domain_banner_dismissed_until";
const REFRESH_MS = 5 * 60 * 1000; // 5 min

/**
 * Czerwony banner u gory ekranu:
 * - domeny wygasajace (aftermarket)
 * - portale niedzialajace (service_outage)
 * - publikacje failujace (publication_failed)
 * - widoczny dla zalogowanych adminow
 * - "X" chowa banner na 8h (localStorage)
 */
export default function DomainExpiringBanner({ user }) {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (until && Date.now() < until) setDismissed(true);
  }, []);

  useEffect(() => {
    if (user?.role !== "admin") return;
    let cancelled = false;

    const fetch = async () => {
      try {
        const [aft, mon] = await Promise.all([
          axios.get(`${API}/aftermarket/expiring`, { params: { days: 30, include_autorenew: false } }).catch(() => null),
          axios.get(`${API}/monitor/stats`).catch(() => null),
        ]);
        if (cancelled) return;
        if (aft) setData(aft.data);
        if (mon) setStats(mon.data);
      } catch (_) {
        // silent
      }
    };
    fetch();
    const id = setInterval(fetch, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user?.role]);

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 8 * 60 * 60 * 1000));
    setDismissed(true);
  };

  if (user?.role !== "admin") return null;
  if (dismissed) return null;

  const all = data?.domains || [];
  const inQuarantine = all.filter((d) => d.lifecycle_state === "expired_in_quarantine").length;
  const expiringSoon = all.filter((d) => d.lifecycle_state === "expiring_soon").length;
  const within7 = all.filter((d) => d.days_left != null && d.days_left >= 0 && d.days_left <= 7).length;
  const lost = all.filter((d) => d.lifecycle_state === "lost").length;

  const byType = stats?.by_type || {};
  const serviceOutage = byType.service_outage || 0;
  const publicationFailed = byType.publication_failed || 0;
  const suspiciousContent = byType.suspicious_content || 0;

  const hasDomainAlerts = inQuarantine + expiringSoon + within7 + lost > 0;
  const hasServiceAlerts = serviceOutage + publicationFailed + suspiciousContent > 0;

  if (!hasDomainAlerts && !hasServiceAlerts) return null;

  return (
    <div className="bg-red-600 text-white text-sm" data-testid="domain-expiring-banner">
      <div className="px-4 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span className="font-medium">
            {hasDomainAlerts && (
              <>
                Domeny Epidemos:
                {inQuarantine > 0 && (
                  <Link to="/aftermarket-domains" className="ml-2 bg-red-900 px-2 py-0.5 rounded-full text-xs font-bold animate-pulse hover:bg-red-950">
                    🚨 {inQuarantine} W KWARANTANNIE (do utraty!)
                  </Link>
                )}
                {expiringSoon > 0 && (
                  <Link to="/aftermarket-domains" className="ml-2 bg-red-700 px-2 py-0.5 rounded-full text-xs hover:bg-red-800">
                    {expiringSoon} wygasa &lt;30 dni
                  </Link>
                )}
                {within7 > 0 && (
                  <Link to="/aftermarket-domains" className="ml-2 bg-red-800 px-2 py-0.5 rounded-full text-xs hover:bg-red-900">
                    {within7} w ciagu 7 dni!
                  </Link>
                )}
                {lost > 0 && (
                  <Link to="/aftermarket-domains" className="ml-2 bg-slate-700 px-2 py-0.5 rounded-full text-xs hover:bg-slate-800">
                    {lost} UTRACONE
                  </Link>
                )}
              </>
            )}
            {hasServiceAlerts && (
              <>
                {hasDomainAlerts && <span className="ml-3 text-white/50">·</span>}
                <span className={hasDomainAlerts ? "ml-2" : ""}>Awarie:</span>
                {serviceOutage > 0 && (
                  <Link to="/aftermarket-domains#outages" className="ml-2 bg-red-900 px-2 py-0.5 rounded-full text-xs font-bold animate-pulse hover:bg-red-950">
                    🚨 {serviceOutage} portali nie działa
                  </Link>
                )}
                {publicationFailed > 0 && (
                  <Link to="/aftermarket-domains#publication-failures" className="ml-2 bg-orange-700 px-2 py-0.5 rounded-full text-xs hover:bg-orange-800">
                    {publicationFailed} fail publikacji
                  </Link>
                )}
                {suspiciousContent > 0 && (
                  <Link to="/ai-monitor" className="ml-2 bg-yellow-700 px-2 py-0.5 rounded-full text-xs hover:bg-yellow-800">
                    {suspiciousContent} podejrzanej treści
                  </Link>
                )}
              </>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={hasServiceAlerts && !hasDomainAlerts ? "/ai-monitor" : "/aftermarket-domains"}
            className="bg-white text-red-700 hover:bg-red-50 px-3 py-1 rounded-md text-xs font-semibold flex items-center gap-1"
          >
            Zarzadzaj <ChevronRight className="w-3 h-3" />
          </Link>
          <button
            onClick={handleDismiss}
            className="text-white/80 hover:text-white p-1"
            aria-label="Ukryj na 8 godzin"
            title="Ukryj na 8 godzin"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
